import AdminService from '../../services/AdminService';
import UserService from '../../services/UserService';
import DeviceService from '../../services/DeviceService';
import { Group } from '../../models/Group';
import { DeviceMgmt } from '../../models/DeviceMgmt';
import {
  ProvisionedUserResult,
  UserMgmt,
  userMgmtFromApi,
} from '../../models/UserMgmt';
import { ActiveFor } from '../../utils/UserExpiry';
import {
  UserUpsertRequest,
  replaceUser,
  replaceUserInGroup,
  toUpsertRequest,
} from '../../utils/UserMgmtApi';

/** EN-1883: payload of the reactivate / extend actions. */
export interface UserPeriodRequest {
  userId: string;
  activeFor?: ActiveFor;
  /** Super admin: the group shown; a user of another group is not added. */
  groupId?: number | null;
}

export interface AdminState {
  groups: Group[];
  users: UserMgmt[] | null;
  agms: DeviceMgmt[] | null;
  allAgms: DeviceMgmt[] | null;
}

export default {
  state: {
    groups: [],
    users: null,
    agms: [],
    allAgms: [],
  } as AdminState,
  mutations: {
    SET_GROUPS(state: AdminState, groups: Group[]) {
      state.groups = groups;
    },
    SET_USERS(state: AdminState, users: any) {
      state.users = users;
    },
    SET_AGMS(state: AdminState, agms: DeviceMgmt[]) {
      state.agms = agms;
    },
    SET_ALL_AGMS(state: AdminState, agms: DeviceMgmt[]) {
      state.allAgms = agms;
    },
  },
  actions: {
    getGroups(context: any, payload: any) {
      AdminService.getGroups()
        .then((response) => {
          const groups: Group[] = [];
          for (const group of response.data) {
            groups.push(new Group(group.id, group.groupName));
          }
          context.commit('SET_GROUPS', groups);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    upsertGroup(context: any, group: Group) {
      AdminService.postGroup(group)
        .then((response) => {
          if (response.data) {
            const groupResponse = new Group(
              response.data.id,
              response.data.groupName
            );
            const updateGroups = context.state.groups.filter(
              (g: Group) => g.id !== group.id
            );
            updateGroups.push(groupResponse);
            context.commit('SET_GROUPS', updateGroups);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    deleteGroup(context: any, groupId: number) {
      AdminService.deleteGroup(groupId)
        .then((response) => {
          if (response.status === 200) {
            const updateGroups = context.state.groups.filter(
              (g: Group) => g.id !== groupId
            );
            context.commit('SET_GROUPS', updateGroups);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },

    // EN-1883: returns the request, so a screen can wait for the re-fetch
    getLocalUsers(context: any) {
      return UserService.getUsers()
        .then((response) => {
          const users: UserMgmt[] = [];
          let groupId = 0;
          for (const user of response.data) {
            users.push(userMgmtFromApi(user));
            groupId = user.group;
          }
          context.commit('SET_GROUPS', [new Group(groupId, '')]);
          context.commit('SET_USERS', users);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async upsertLocalUser(
      context: any,
      payload: UserMgmt | UserUpsertRequest
    ): Promise<ProvisionedUserResult> {
      // EN-1883: activeFor is sent only when the admin picked a period.
      const request = toUpsertRequest(payload);
      const response = await UserService.postUser(
        request.user,
        request.activeFor
      );
      const userResponse = userMgmtFromApi(response.data);
      context.commit(
        'SET_USERS',
        replaceUser(context.state.users, userResponse)
      );

      return {
        user: userResponse,
        temporaryPassword: response.data.temporaryPassword || '',
      };
    },
    async regenerateLocalTemporaryPassword(
      context: any,
      userId: string
    ): Promise<ProvisionedUserResult> {
      const response = await UserService.regenerateTemporaryPassword(userId);
      const userResponse = userMgmtFromApi(response.data);
      const updateUsers = (context.state.users || []).filter(
        (existingUser: UserMgmt) => existingUser.id !== userId
      );
      updateUsers.push(userResponse);
      context.commit('SET_USERS', updateUsers);

      return {
        user: userResponse,
        temporaryPassword: response.data.temporaryPassword || '',
      };
    },
    deleteLocalUser(context: any, userId: string) {
      UserService.deleteUser(userId)
        .then((response) => {
          if (response.status === 200) {
            const updateUsers = context.state.users.filter(
              (u: UserMgmt) => u.id !== userId
            );
            context.commit('SET_USERS', updateUsers);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async deactivateLocalUser(context: any, userId: string): Promise<UserMgmt> {
      const response = await UserService.deactivate(userId);
      const updated = userMgmtFromApi(response.data);
      context.commit('SET_USERS', replaceUser(context.state.users, updated));
      return updated;
    },
    async reactivateLocalUser(
      context: any,
      request: UserPeriodRequest
    ): Promise<UserMgmt> {
      const response = await UserService.reactivate(
        request.userId,
        request.activeFor
      );
      const updated = userMgmtFromApi(response.data);
      context.commit('SET_USERS', replaceUser(context.state.users, updated));
      return updated;
    },
    async extendLocalUser(
      context: any,
      request: UserPeriodRequest
    ): Promise<UserMgmt> {
      const response = await UserService.extend(
        request.userId,
        request.activeFor as ActiveFor
      );
      const updated = userMgmtFromApi(response.data);
      context.commit('SET_USERS', replaceUser(context.state.users, updated));
      return updated;
    },

    // EN-1883: returns the request, so a screen can wait for the re-fetch
    getAdminUsers(context: any, groupId: number) {
      return AdminService.getUsers(groupId)
        .then((response) => {
          const users: UserMgmt[] = [];
          for (const user of response.data) {
            users.push(userMgmtFromApi(user, groupId));
          }
          context.commit('SET_USERS', users);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async upsertAdminUser(
      context: any,
      payload: UserMgmt | UserUpsertRequest
    ): Promise<ProvisionedUserResult> {
      // EN-1883: activeFor is sent only when the admin picked a period.
      const request = toUpsertRequest(payload);
      const response = await AdminService.postUser(
        request.user,
        request.activeFor
      );
      const userResponse = userMgmtFromApi(response.data);
      context.commit(
        'SET_USERS',
        replaceUser(context.state.users, userResponse)
      );

      return {
        user: userResponse,
        temporaryPassword: response.data.temporaryPassword || '',
      };
    },
    async regenerateAdminTemporaryPassword(
      context: any,
      userId: string
    ): Promise<ProvisionedUserResult> {
      const response = await AdminService.regenerateTemporaryPassword(userId);
      const userResponse = userMgmtFromApi(response.data);
      const updateUsers = (context.state.users || []).filter(
        (existingUser: UserMgmt) => existingUser.id !== userId
      );
      updateUsers.push(userResponse);
      context.commit('SET_USERS', updateUsers);

      return {
        user: userResponse,
        temporaryPassword: response.data.temporaryPassword || '',
      };
    },
    deleteAdminUser(context: any, userId: string) {
      AdminService.deleteUser(userId)
        .then((response) => {
          if (response.status === 200) {
            const updateUsers = context.state.users.filter(
              (u: UserMgmt) => u.id !== userId
            );
            context.commit('SET_USERS', updateUsers);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async deactivateAdminUser(context: any, userId: string): Promise<UserMgmt> {
      const response = await AdminService.deactivate(userId);
      const updated = userMgmtFromApi(response.data);
      context.commit('SET_USERS', replaceUser(context.state.users, updated));
      return updated;
    },
    async reactivateAdminUser(
      context: any,
      request: UserPeriodRequest
    ): Promise<UserMgmt> {
      const response = await AdminService.reactivate(
        request.userId,
        request.activeFor
      );
      const updated = userMgmtFromApi(response.data);
      context.commit(
        'SET_USERS',
        replaceUserInGroup(context.state.users, updated, request.groupId)
      );
      return updated;
    },
    async extendAdminUser(
      context: any,
      request: UserPeriodRequest
    ): Promise<UserMgmt> {
      const response = await AdminService.extend(
        request.userId,
        request.activeFor as ActiveFor
      );
      const updated = userMgmtFromApi(response.data);
      context.commit(
        'SET_USERS',
        replaceUserInGroup(context.state.users, updated, request.groupId)
      );
      return updated;
    },

    getAllAgms(context: any) {
      DeviceService.getDevices()
        .then((response) => {
          const agms: DeviceMgmt[] = [];
          for (const d of response.data) {
            // TODO set imei when merged with iridium
            const a = new DeviceMgmt(
              d.uId,
              d.group,
              d.lat,
              d.lon,
              undefined,
              d.deviceType
            );
            a.group = d.groupId;
            agms.push(a);
          }
          context.commit('SET_ALL_AGMS', agms);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getAdminAgms(context: any, groupId: number) {
      AdminService.getAgms(groupId)
        .then((response) => {
          const agms: DeviceMgmt[] = [];
          for (const d of response.data) {
            // TODO set imei when merged with iridium
            const a = new DeviceMgmt(
              d.uId,
              d.group,
              d.lat,
              d.lon,
              undefined,
              d.deviceType
            );
            a.group = d.groupId;
            agms.push(a);
          }
          context.commit('SET_AGMS', agms);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    deleteAgm(context: any, uid: number) {
      AdminService.deleteAgm(uid)
        .then((response) => {
          if (response.status === 200) {
            const updateAgms = context.state.agms.filter(
              (d: DeviceMgmt) => d.uid !== uid
            );
            context.commit('SET_AGMS', updateAgms);
            context.dispatch('getAllAgms');
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    addAgm(context: any, agm: DeviceMgmt) {
      AdminService.addAgm(agm)
        .then((response) => {
          if (response.status === 201) {
            const updateAgms = context.state.agms;
            updateAgms.push(agm);
            context.commit('SET_AGMS', updateAgms);
            context.dispatch('getAllAgms');
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
};
